import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CheckRounded from '@mui/icons-material/CheckRounded';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePedigreeSaveIndicator } from './usePedigreeSaveIndicator';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// Feature flag (default OFF). With it off, the pedigree canvas renders exactly
// as it does today (the old inline save text beside the "You are editing" chip).
// With it on, this transient indicator stacks as a lighter second line beneath
// the chip. Remove this flag and the flag-off branches once the stacked layout
// has rolled out, to avoid long-lived dead code.
export const ENABLE_STACKED_SAVE_INDICATOR = true;

/**
 * Transient save-status line for the pedigree canvas. Borderless: a spinning
 * teal wheel with "Saving", then a green tick with "Saved just now", both in
 * muted grey text, then it fades out. Presentational only; the caller owns the
 * positioning wrapper (so its mount/unmount never reflows the chip above it).
 *
 * @param {{ isSaving: boolean }} props
 */
export function PedigreeSaveIndicator({ isSaving }) {
  const t = useAutoTranslation();

  const state = usePedigreeSaveIndicator(isSaving);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  if (state === 'idle') return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        color: 'text.secondary', // muted grey
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        transition: reduceMotion ? 'none' : 'opacity 200ms ease'
      }}
    >
      {state === 'saving' ? (
        <CircularProgress
          size={14}
          thickness={5}
          // Brand teal (var(--primary-color), #2ba9bf). Under reduced motion a
          // static three-quarter ring is shown so the wheel never spins.
          variant={reduceMotion ? 'determinate' : 'indeterminate'}
          value={reduceMotion ? 75 : undefined}
          sx={{ color: 'var(--primary-color)' }}
          aria-hidden
        />
      ) : (
        <CheckRounded
          // Success green (var(--color-active-fg), #2a6a20), matching the FHQ
          // family-details saved indicator.
          sx={{ fontSize: 16, color: 'var(--color-active-fg)' }}
          aria-hidden
        />
      )}
      <Typography component="span" variant="inherit">
        {state === 'saving' ? t('Saving') : t('Saved just now')}
      </Typography>
    </Box>
  );
}
